import { ROUTES_EMPLOYEES, ROUTES_SUPERVISORS } from '@/app/data/route_constants';
import router from '@/router';
import { PROFILE_TYPES, ProfileRecord } from '@/app/data/model_constants';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { zigguParentURL, zigguSubdomainURL } from '@/app/util/urls';
import useGlobalConfirm from '@/composables/useGlobalConfirm';

export default function useProfileImpersonation() {
  const store = useStore();
  const { confirm } = useGlobalConfirm();
  const { t } = useI18n();

  // TODO: not complete as-is, see ApplicationController#authorize_impersonation, also check employee role maintainer
  const canImpersonateProfile = (current_profile, profile) =>
    current_profile.type === PROFILE_TYPES.SUPERVISOR ||
    (current_profile.type === PROFILE_TYPES.EMPLOYEE && profile.type === PROFILE_TYPES.PARTNER) ||
    (current_profile.type === PROFILE_TYPES.EMPLOYEE && profile.type === PROFILE_TYPES.CUSTOMER);

  const isProfileActive = (profile) => {
    if (_.includes(ProfileRecord.STATUSES_ACTIVE, profile.attributes.status)) return true;

    confirm({
      message: t('contact.labels.impersonateInactiveProfile'),
      header: t('modal.labels.ohNo'),
      accept_label: t('modal.actions.gotIt'),
    });
    return false;
  };

  const buildImpersonationURL = (profile, tenantable, { return_to = null } = {}) => {
    const subdomain = tenantable.attributes.subdomain;
    const url_base =
      profile.type === PROFILE_TYPES.CUSTOMER
        ? zigguSubdomainURL(subdomain, `/customer/${subdomain}`)
        : zigguParentURL(`/${profile.type.toLowerCase()}/${subdomain}`);

    const url = new URL(url_base);
    url.searchParams.set('_profile_type', profile.type);
    url.searchParams.set('_profile_id', profile.id);
    if (return_to) url.searchParams.set('_return_to', return_to);

    return url.toString();
  };

  const impersonationURL = (profile, tenantable, { validate = true, return_to = null } = {}) => {
    if (!tenantable) return null;
    if (!canImpersonateProfile(store.getters.current_profile, profile)) return null;
    if (validate && !isProfileActive(profile)) return null;

    return buildImpersonationURL(profile, tenantable, { return_to });
  };

  const supervisorURL = (current_profile) => {
    const subdomain = PROFILE_TYPES.SUPERVISOR.toLowerCase();
    const url_base = zigguSubdomainURL(subdomain);
    const url_path = router.resolve({
      name: ROUTES_SUPERVISORS.USER_PROFILE_INFO,
      params: {
        user_id: current_profile.attributes.user_id,
        profile_id: current_profile.id,
        profile_type: current_profile.type,
      },
    }).href;
    return `${url_base}${url_path}`;
  };

  const impersonate = (profile, tenantable, { only_url = false, open_in_new_tab = false, return_to = null } = {}) => {
    try {
      const url_return_to = return_to || window.location.href;
      const url = impersonationURL(profile, tenantable, { validate: true, return_to: url_return_to });
      if (!url) return undefined;

      if (only_url) return url;
      if (open_in_new_tab) {
        const newWindow = window.open(url, '_blank', 'noopener,noreferrer');
        if (newWindow === null) {
          throw new Error('Unable to open new window. Please check if popup blocker is enabled.');
        }
      } else {
        window.location.href = url;
      }
    } catch {
      return undefined;
    }
    return undefined;
  };

  const clearImpersonationParams = () => {
    sessionStorage.removeItem('_profile_id');
    sessionStorage.removeItem('_profile_type');
    sessionStorage.removeItem('_impersonation_return_to');
  };

  const stopImpersonation = () => {
    const url_return_to = sessionStorage.getItem('_impersonation_return_to');
    clearImpersonationParams();

    if (url_return_to) {
      window.location.href = url_return_to;
      return;
    }

    if (store.getters.current_profile_true.type === PROFILE_TYPES.SUPERVISOR) {
      window.location.href = supervisorURL(store.getters.current_profile);
    } else if (store.getters.current_profile_true.type === PROFILE_TYPES.EMPLOYEE) {
      const subdomain = store.getters.current_client.attributes.subdomain;
      const url_base = zigguParentURL();
      const url_path = router.resolve({
        name: ROUTES_EMPLOYEES.CLIENT_CONTACTS_TYPE,
        params: {
          subdomain,
          profile_id: store.getters.current_profile.id,
          profile_type: store.getters.current_profile.type,
        },
      }).href;
      window.location.href = `${url_base}${url_path}`;
    }
  };

  return {
    clearImpersonationParams,
    impersonate,
    impersonationURL,
    isProfileActive,
    stopImpersonation,
    supervisorURL,
  };
}
